import type { GraphVisualizationFrame, SaleskoOAuthProvider } from "@salesko/contracts";
import { CheckCircle2, DatabaseZap, GitBranchPlus, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@salesko/design-system";
import type { ReactNode } from "react";
import { useI18n } from "../../i18n";
import { AuthProviderButtons } from "./AuthProviderButtons";

type OnboardingPanelProps = {
  availableAuthProviders: SaleskoOAuthProvider[];
  frame: GraphVisualizationFrame;
  hasCommittedFrame: boolean;
  hasStoredSession: boolean;
  isFirstRun: boolean;
  onDismissFirstRun: () => void;
  onSignIn: (provider: SaleskoOAuthProvider) => void;
  onOpenAgent: () => void;
  onOpenSettings: () => void;
  readOnly: boolean;
};

export function OnboardingPanel({
  availableAuthProviders,
  frame,
  hasCommittedFrame,
  hasStoredSession,
  isFirstRun,
  onDismissFirstRun,
  onSignIn,
  onOpenAgent,
  onOpenSettings,
  readOnly
}: OnboardingPanelProps) {
  const { t } = useI18n();
  const nodeCount = frame.graph.nodes.length;
  const edgeCount = frame.graph.edges.length;

  return (
    <div className="onboarding-panel">
      <div className="tenant-admin-header">
        <span className="tenant-admin-icon" aria-hidden="true">
          <Sparkles size={26} />
        </span>
        <div>
          <p className="eyebrow">{t.onboarding.eyebrowGuide}</p>
          <h2>{t.onboarding.heading}</h2>
          <span>{t.onboarding.graphSummary(nodeCount, edgeCount)}</span>
        </div>
      </div>

      <section className="detail-section onboarding-section" aria-label={t.onboarding.firstGraphSectionLabel}>
        <div className="section-heading-row">
          <p className="eyebrow">{t.onboarding.eyebrowFirstGraph}</p>
          <Sparkles size={15} />
        </div>
        <div className="onboarding-step-list">
          <OnboardingStep
            icon={<Sparkles size={17} />}
            title={t.onboarding.captureContextTitle}
            body={t.onboarding.captureContextBody}
          />
          <OnboardingStep
            icon={<GitBranchPlus size={17} />}
            title={t.onboarding.reviewGraphTitle}
            body={t.onboarding.reviewGraphBody}
          />
          <OnboardingStep
            icon={<DatabaseZap size={17} />}
            title={t.onboarding.keepFrameTitle}
            body={t.onboarding.keepFrameBody}
          />
        </div>
        <div className="onboarding-actions">
          <Button
            disabled={readOnly}
            fullWidth
            iconLeft={<Sparkles size={16} />}
            onClick={onOpenAgent}
            style={{ marginTop: "10px" }}
            variant="primary"
          >
            {t.onboarding.openAgent}
          </Button>
          {isFirstRun ? (
            <Button iconLeft={<CheckCircle2 size={16} />} onClick={onDismissFirstRun} variant="secondary">
              {t.onboarding.startInGraph}
            </Button>
          ) : null}
        </div>
      </section>

      <section className="detail-section onboarding-section" aria-label={t.onboarding.workspaceStatusLabel}>
        <div className="section-heading-row">
          <p className="eyebrow">{t.onboarding.workspaceStatusLabel}</p>
          <ShieldCheck size={15} />
        </div>
        <div className="onboarding-status-list">
          <StatusLine
            ok={hasStoredSession}
            label={hasStoredSession ? t.onboarding.signedInStatus : t.onboarding.signInToPersistStatus}
          />
          <StatusLine
            ok={hasCommittedFrame}
            label={hasCommittedFrame ? t.onboarding.frameHistoryActiveStatus : t.onboarding.noCommittedFrameStatus}
          />
          <StatusLine
            ok={!readOnly}
            label={readOnly ? t.onboarding.readOnlySharedViewStatus : t.onboarding.workspaceEditableStatus}
          />
        </div>
        {!readOnly && !hasStoredSession ? (
          <AuthProviderButtons
            ariaLabel={t.onboarding.signInOptionsLabel}
            availableAuthProviders={availableAuthProviders}
            buttonClassName="secondary-button onboarding-auth-button"
            className="onboarding-auth-providers"
            onSignIn={onSignIn}
          />
        ) : null}
        {!readOnly && hasStoredSession ? (
          <button className="secondary-button" type="button" onClick={onOpenSettings}>
            <ShieldCheck size={16} />
            <span>{t.common.settings}</span>
          </button>
        ) : null}
      </section>
    </div>
  );
}

function OnboardingStep({ body, icon, title }: { body: string; icon: ReactNode; title: string }) {
  return (
    <article className="onboarding-step">
      <span className="onboarding-step-icon" aria-hidden="true">
        {icon}
      </span>
      <div>
        <strong>{title}</strong>
        <p>{body}</p>
      </div>
    </article>
  );
}

function StatusLine({ label, ok }: { label: string; ok: boolean }) {
  return (
    <p className={ok ? "onboarding-status is-ok" : "onboarding-status"}>
      <CheckCircle2 size={15} />
      <span>{label}</span>
    </p>
  );
}
